import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "@/App";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import {
  Share2, Copy, Gift, Users, Crown, Award, Check, ChevronRight,
  MessageCircle, Mail, Link2, Send
} from "lucide-react";
import { shareToApp, shareViaWebAPI } from "@/hooks/useNativeShare";

export default function ReferralPanel({ onClose }) {
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [copied, setCopied] = useState(false);
  const [claiming, setClaiming] = useState(null);

  useEffect(() => {
    fetchReferralStats();
  }, []);

  const fetchReferralStats = async () => {
    try {
      const res = await axios.get(`${API}/referrals/stats`);
      setStats(res.data);
    } catch (error) {
      console.error("Failed to fetch referral stats:", error);
      toast.error("Failed to load referral data");
    } finally {
      setLoading(false);
    }
  };

  const copyReferralLink = () => {
    const link = `${window.location.origin}/register?ref=${stats?.referral_code}`;
    navigator.clipboard.writeText(link);
    setCopied(true);
    toast.success("Referral link copied!");
    setTimeout(() => setCopied(false), 2000);
    
    // Track share
    axios.post(`${API}/referrals/share`, {
      share_type: "referral_link",
      metadata: { method: "copy" }
    }).catch(() => {});
  };

  const shareVia = (platform) => {
    const link = `${window.location.origin}/register?ref=${stats?.referral_code}`;
    const text = "Join me on DuoLyst - the best way to plan adventures with your partner! Use my link:";
    
    shareToApp(platform, text, link, "Join me on DuoLyst!");
    
    // Track share
    axios.post(`${API}/referrals/share`, {
      share_type: "referral_link",
      metadata: { method: platform }
    }).catch(() => {});
  };

  const claimReward = async (rewardId) => {
    setClaiming(rewardId);
    try {
      const res = await axios.post(`${API}/referrals/claim`, { reward_id: rewardId });
      if (res.data.success) {
        toast.success(`Reward claimed: ${res.data.reward.name}`);
        fetchReferralStats();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to claim reward");
    } finally {
      setClaiming(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="text-center">
        <div className="w-16 h-16 bg-gradient-to-br from-primary to-secondary rounded-full flex items-center justify-center mx-auto mb-4">
          <Gift className="w-8 h-8 text-white" />
        </div>
        <h2 className="text-2xl font-serif font-bold">Invite Friends & Earn Rewards</h2>
        <p className="text-muted-foreground mt-2">
          Share DuoLyst and unlock premium features for free
        </p>
      </div>

      {/* Referral Code Card */}
      <Card className="bg-gradient-to-r from-primary/10 to-secondary/10 border-primary/20">
        <CardContent className="p-6">
          <div className="text-sm text-muted-foreground mb-2">Your Referral Code</div>
          <div className="flex items-center gap-3">
            <div className="flex-1 bg-background rounded-lg px-4 py-3 font-mono text-xl font-bold tracking-wider">
              {stats?.referral_code}
            </div>
            <Button
              onClick={copyReferralLink}
              variant={copied ? "default" : "outline"}
              className="h-12 px-4"
            >
              {copied ? <Check className="w-5 h-5" /> : <Copy className="w-5 h-5" />}
            </Button>
          </div>
          
          {/* Share Buttons */}
          <div className="flex items-center justify-center gap-2 mt-4 flex-wrap">
            <Button
              variant="outline"
              size="icon"
              className="rounded-full w-10 h-10 bg-green-500/10 hover:bg-green-500/20 border-green-500/30"
              onClick={() => shareVia("whatsapp")}
              data-testid="share-whatsapp"
            >
              <MessageCircle className="w-4 h-4 text-green-600" />
            </Button>
            <Button
              variant="outline"
              size="icon"
              className="rounded-full w-10 h-10"
              onClick={() => shareVia("twitter")}
              data-testid="share-twitter"
            >
              <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
            </Button>
            <Button
              variant="outline"
              size="icon"
              className="rounded-full w-10 h-10"
              onClick={() => shareVia("facebook")}
              data-testid="share-facebook"
            >
              <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
            </Button>
            <Button
              variant="outline"
              size="icon"
              className="rounded-full w-10 h-10 bg-sky-500/10 hover:bg-sky-500/20 border-sky-500/30"
              onClick={() => shareVia("telegram")}
              data-testid="share-telegram"
            >
              <Send className="w-4 h-4 text-sky-500" />
            </Button>
            <Button
              variant="outline"
              size="icon"
              className="rounded-full w-10 h-10"
              onClick={() => shareVia("messages")}
              data-testid="share-messages"
            >
              <MessageCircle className="w-4 h-4" />
            </Button>
            <Button
              variant="outline"
              size="icon"
              className="rounded-full w-10 h-10"
              onClick={() => shareVia("email")}
              data-testid="share-email"
            >
              <Mail className="w-4 h-4" />
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4 text-center">
            <div className="text-2xl font-bold">{stats?.stats?.total_sent || 0}</div>
            <div className="text-xs text-muted-foreground">Invites Sent</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <div className="text-2xl font-bold text-primary">{stats?.stats?.total_accepted || 0}</div>
            <div className="text-xs text-muted-foreground">Joined</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <div className="text-2xl font-bold text-green-600">{stats?.stats?.total_activated || 0}</div>
            <div className="text-xs text-muted-foreground">Active</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <div className="text-2xl font-bold text-amber-500">{stats?.stats?.total_upgraded || 0}</div>
            <div className="text-xs text-muted-foreground">Upgraded</div>
          </CardContent>
        </Card>
      </div>

      {/* Progress to Next Reward */}
      {stats?.next_reward && (
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <span className="text-2xl">{stats.next_reward.badge_emoji}</span>
                <div>
                  <div className="font-semibold">{stats.next_reward.name}</div>
                  <div className="text-xs text-muted-foreground">{stats.next_reward.description}</div>
                </div>
              </div>
              <div className="text-right">
                <div className="font-bold">{stats.next_reward.progress}/{stats.next_reward.requirement}</div>
                <div className="text-xs text-muted-foreground">{stats.next_reward.remaining} more needed</div>
              </div>
            </div>
            <Progress 
              value={(stats.next_reward.progress / stats.next_reward.requirement) * 100} 
              className="h-2"
            />
          </CardContent>
        </Card>
      )}

      {/* Ambassador Status */}
      {stats?.is_ambassador && (
        <Card className="bg-gradient-to-r from-amber-500/20 to-amber-600/20 border-amber-500/30">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="w-12 h-12 bg-gradient-to-br from-amber-400 to-amber-600 rounded-full flex items-center justify-center">
              <Crown className="w-6 h-6 text-white" />
            </div>
            <div className="flex-1">
              <div className="font-semibold text-amber-600">
                {stats.ambassador_level === "gold" ? "Gold" : 
                 stats.ambassador_level === "silver" ? "Silver" : "Bronze"} Ambassador
              </div>
              <div className="text-sm text-muted-foreground">
                Thank you for spreading the DuoLyst love!
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Rewards List */}
      <div>
        <h3 className="font-semibold mb-3">Referral Rewards</h3>
        <div className="space-y-2">
          {stats?.rewards?.map((reward) => (
            <Card 
              key={reward.id} 
              className={`transition-all ${reward.unlocked ? 'bg-green-500/5 border-green-500/20' : 'opacity-70'}`}
            >
              <CardContent className="p-4 flex items-center gap-4">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center text-xl ${
                  reward.unlocked ? 'bg-green-500/20' : 'bg-muted'
                }`}>
                  {reward.badge_emoji}
                </div>
                <div className="flex-1">
                  <div className="font-medium flex items-center gap-2">
                    {reward.name}
                    {reward.claimed && <Check className="w-4 h-4 text-green-500" />}
                  </div>
                  <div className="text-xs text-muted-foreground">
                    {reward.reward_type === "free_pro_days" && `${reward.reward_value} days free Pro`}
                    {reward.reward_type === "duo_discount" && `${reward.reward_value}% Duo discount`}
                    {reward.reward_type === "premium_map_skin" && "Exclusive map theme"}
                    {reward.reward_type === "ambassador_badge" && `${reward.reward_value} Ambassador badge`}
                  </div>
                </div>
                <div className="text-right">
                  {reward.unlocked && !reward.claimed ? (
                    <Button 
                      size="sm" 
                      onClick={() => claimReward(reward.id)}
                      disabled={claiming === reward.id}
                    >
                      {claiming === reward.id ? "..." : "Claim"}
                    </Button>
                  ) : reward.claimed ? (
                    <span className="text-xs text-green-600 font-medium">Claimed</span>
                  ) : (
                    <span className="text-xs text-muted-foreground">
                      {reward.progress}/{reward.requirement}
                    </span>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </div>
  );
}
